import { Injectable } from '@angular/core';

export interface BounceBackOrderTracking {
  orderId: string;
  customerId: number;
  couponLevel: number; // 10, 15, or 20
  bbcBonusTotal: number;
  appliedAt: Date;
  orderTotal: number;
}

export interface BounceBackLogEntry {
  timestamp: string;
  type: 'validation_error' | 'api_error' | 'reward_earned' | 'reward_used' | 'info';
  message: string;
  details: any;
  customerId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class BounceBackUtilsService {

  constructor() {}

  /**
   * Calculate BBC bonus amount for order items based on coupon level
   * This is for commission tracking only - actual rewards come from Paymenture API
   */
  calculateBBCBonus(items: any[], couponPercentage: number): number {
    if (!items || items.length === 0) {
      return 0;
    }

    return items.reduce((total, item) => {
      const quantity = item.Quantity || 1;
      let bbcAmount = 0;

      // Get BBC amount based on coupon percentage
      switch (couponPercentage) {
        case 10:
          bbcAmount = item.BBC10 || 0;
          break;
        case 15:
          bbcAmount = item.BBC15 || 0;
          break;
        case 20:
          bbcAmount = item.BBC20 || 0;
          break;
        default:
          bbcAmount = 0;
      }

      return total + (bbcAmount * quantity);
    }, 0);
  }

  /**
   * Validate if coupon can be used (member-only check)
   */
  validateMemberAccess(customerId: number, customerType: number): { isValid: boolean; message?: string } {
    if (!customerId || customerId <= 0) {
      return {
        isValid: false,
        message: 'Please log in to use BounceBack rewards. This feature is available for members only.'
      };
    }

    // Additional customer type validation if needed
    // For now, all logged-in customers can use bounceback
    return {
      isValid: true
    };
  }

  /**
   * Check if it's a valid bounceback coupon code
   */
  isBounceBackCoupon(couponCode: string): boolean {
    if (!couponCode) return false;
    const code = couponCode.toLowerCase().trim();
    // Only match exact "bounce back" or variations with proper spacing
    return code === 'bounce back' || code === 'bounceback' || code === 'bounce-back';
  }

  /**
   * Generate order tracking data for bounceback usage (commission tracking only)
   */
  createOrderTracking(
    orderId: string,
    customerId: number,
    couponLevel: number,
    items: any[],
    orderTotal: number
  ): BounceBackOrderTracking {
    const bbcBonusTotal = this.calculateBBCBonus(items, couponLevel);

    return {
      orderId,
      customerId,
      couponLevel,
      bbcBonusTotal,
      appliedAt: new Date(),
      orderTotal
    };
  }

  /**
   * Validate order minimum requirements for bounceback usage
   */
  validateOrderRequirements(
    orderTotal: number,
    items: any[],
    storeId: string
  ): { isValid: boolean; message?: string } {
    // Check if order is in marketplace store (as per BRD requirement)
    const MARKETPLACE_STORE = 'CE4A55BCFA'; // From constants
    if (storeId !== MARKETPLACE_STORE) {
      return {
        isValid: false,
        message: 'BounceBack rewards can only be used in the marketplace store.'
      };
    }

    // Check minimum order amount (configurable)
    const minimumOrderAmount = this.getMinimumOrderAmount();
    if (orderTotal < minimumOrderAmount) {
      return {
        isValid: false,
        message: `Minimum order amount of $${minimumOrderAmount} required for BounceBack rewards.`
      };
    }

    // Check if cart has valid items
    if (!items || items.length === 0) {
      return {
        isValid: false,
        message: 'Cart is empty. Add items to use BounceBack rewards.'
      };
    }

    return {
      isValid: true
    };
  }

  /**
   * Get minimum order amount from configuration
   */
  private getMinimumOrderAmount(): number {
    // Check environment configuration first
    if (typeof window !== 'undefined' && (window as any).bouncebackConfig?.minimumOrderAmount) {
      return (window as any).bouncebackConfig.minimumOrderAmount;
    }
    
    // Check localStorage for override
    const storedMin = localStorage.getItem('bounceback_minimum_order');
    if (storedMin) {
      return parseFloat(storedMin);
    }
    
    // Default minimum order amount
    return 25; // Can be changed to 0, 10, 15, etc.
  }

  /**
   * Set minimum order amount for testing/configuration
   */
  setMinimumOrderAmount(amount: number): void {
    localStorage.setItem('bounceback_minimum_order', amount.toString());
  }

  /**
   * Get current minimum order amount
   */
  getCurrentMinimumOrderAmount(): number {
    return this.getMinimumOrderAmount();
  }

  /**
   * Reset minimum order amount to default
   */
  resetMinimumOrderAmount(): void {
    localStorage.removeItem('bounceback_minimum_order');
  }

  /**
   * Log bounceback transaction for debugging and audit
   */
  logBounceBackTransaction(
    type: 'reward_earned' | 'reward_used' | 'validation_error' | 'api_error' | 'info',
    data: any,
    customerId?: number
  ): void {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type,
      customerId,
      data,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

        // Store in localStorage for debugging (limit to last 50 entries)
    try {
      const existingLogs = JSON.parse(localStorage.getItem('bounceback_logs') || '[]');
      existingLogs.push(logEntry);
      
      // Keep only last 50 entries
      if (existingLogs.length > 50) {
        existingLogs.splice(0, existingLogs.length - 50);
      }
      
      localStorage.setItem('bounceback_logs', JSON.stringify(existingLogs));
    } catch (error) {
      console.warn('Failed to store bounceback log:', error);
    }
  }

  /**
   * Get stored bounceback logs for debugging
   */
  getBounceBackLogs(): any[] {
    try {
      return JSON.parse(localStorage.getItem('bounceback_logs') || '[]');
    } catch (error) {
      console.warn('Failed to retrieve bounceback logs:', error);
      return [];
    }
  }

  /**
   * Clear bounceback logs
   */
  clearBounceBackLogs(): void {
    try {
      localStorage.removeItem('bounceback_logs');
    } catch (error) {
      console.warn('Failed to clear bounceback logs:', error);
    }
  }
}
